import { COUNTRY_PHONE_CODES, UNITED_STATES_AND_CANADA_PHONE_CODE } from '@govinity/shared-config';
import { z } from 'zod';
// Phone number validation regex patterns
const PHONE_NUMBER_REGEX = /^[\+]?[1-9][\d]{0,15}$/;
const EXTENSION_REGEX = /^[0-9*#]*$/;
const validatePhoneNumber = (data, options = { isRequired: false }) => {
    if (data.number === null || data.number === undefined || data.number === '') {
        return !options.isRequired;
    }
    // Basic phone number validation
    if (!PHONE_NUMBER_REGEX.test(data.number)) {
        return false;
    }
    // Country-specific validation
    if (data.countryCode === UNITED_STATES_AND_CANADA_PHONE_CODE) {
        // US/Canada numbers should be 10 digits
        return data.number.length === 10;
    }
    // For other countries, just ensure it's a reasonable length
    return data.number.length >= 7 && data.number.length <= 15;
};
const validateExtension = (extension) => {
    if (!extension) {
        return true; // Extension is optional
    }
    return EXTENSION_REGEX.test(extension);
};
// Enhanced schema with country-specific error messages
export const phoneRequiredSchema = z
    .object({
    countryCode: z.enum(COUNTRY_PHONE_CODES, 'Country is required'),
    number: z.string('Phone number is required').min(1, 'Phone number is required'),
    extension: z.string().nullish(),
})
    .transform((data) => {
    // Clean the phone number by removing all non-digit characters except +
    const cleanNumber = data.number.replace(/\D/g, '');
    return {
        ...data,
        number: cleanNumber,
    };
})
    .refine((data) => {
    return validatePhoneNumber(data, { isRequired: true });
}, {
    message: 'Please enter a valid phone number',
    path: ['number'],
})
    .refine((data) => {
    return validateExtension(data.extension);
}, {
    message: 'Please enter a valid extension',
    path: ['extension'],
});
export const phoneOptionalSchema = z
    .object({
    countryCode: z.enum(COUNTRY_PHONE_CODES, 'Country is required'),
    number: z.string().nullish(),
    extension: z.string().nullish(),
})
    .transform((data) => {
    // Clean the phone number by removing all non-digit characters except +
    if (data.number) {
        const cleanNumber = data.number.replace(/\D/g, '');
        return {
            ...data,
            number: cleanNumber,
        };
    }
    return data;
})
    .refine((data) => {
    return validatePhoneNumber(data, { isRequired: false });
}, {
    message: 'Please enter a valid phone number',
    path: ['number'],
})
    .refine((data) => {
    return validateExtension(data.extension);
}, {
    message: 'Please enter a valid extension',
    path: ['extension'],
});
